import axios from 'axios';

// Use relative URL to avoid CORS issues in production
const API = '/api';

// Helper to append module parameter
const withModule = (url, module) => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}module=${module}`;
};

// Helper to append impersonate parameter
const withImpersonate = (url, impersonateUserId) => {
  if (!impersonateUserId) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}impersonate_user_id=${impersonateUserId}`;
};

// Helper to append pagination parameters
const withPagination = (url, page = 1, limit = 50) => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}page=${page}&limit=${limit}`;
};

// Settings (module-specific)
export const getSettings = (module = 'futures') => axios.get(withModule(`${API}/settings`, module));
export const updateSettings = (data, module = 'futures') => axios.put(withModule(`${API}/settings`, module), data);
export const getProfitSplit = (module = 'futures') => axios.get(withModule(`${API}/settings/profit-split`, module));
export const getCurrentSplit = (module = 'futures') => axios.get(withModule(`${API}/settings/current-split`, module));
export const updateProfitSplit = (data, module = 'futures') => axios.post(withModule(`${API}/settings/profit-split`, module), data);
export const getMonthlyCapitals = (module = 'futures') => axios.get(withModule(`${API}/settings/monthly-capitals`, module));
export const addMonthlyCapital = (data, module = 'futures') => axios.post(withModule(`${API}/settings/monthly-capitals`, module), data);
export const deleteMonthlyCapital = (month, module = 'futures') => axios.delete(withModule(`${API}/settings/monthly-capitals/${month}`, module));

// Cycles (module-specific) - with pagination
export const getCycles = (module = 'futures', page = 1, limit = 50) => 
  axios.get(withPagination(withModule(`${API}/cycles`, module), page, limit));
export const createCycle = (data, module = 'futures') => axios.post(withModule(`${API}/cycles`, module), data);
export const deleteCycle = (id) => axios.delete(`${API}/cycles/${id}`);
export const getCycleDetails = (id) => axios.get(`${API}/cycles/${id}/details`);
export const registerPayout = (cycleId, data) => axios.post(`${API}/cycles/${cycleId}/payout`, data);
export const completeCycle = (cycleId, data) => axios.post(`${API}/cycles/${cycleId}/complete`, data);

// CFD Refunds (Rimborsi) - only for CFD module - with pagination
export const registerRefund = (cycleId, data) => axios.post(`${API}/cycles/${cycleId}/refund`, data);
export const getRefunds = (module = 'cfd', page = 1, limit = 50) => 
  axios.get(withPagination(withModule(`${API}/refunds`, module), page, limit));
export const updateRefund = (id, data) => axios.put(`${API}/refunds/${id}`, data);
export const deleteRefund = (id) => axios.delete(`${API}/refunds/${id}`);

// CFD Credits & Awaiting System
export const getCfdCredits = () => axios.get(`${API}/cfd/credits`);
export const updateCfdCredits = (data) => axios.put(`${API}/cfd-credits`, data);
export const cfdProfitProp = (cycleId, data) => axios.post(`${API}/cycles/${cycleId}/profit-prop`, data);
export const cfdRiscuoti = (cycleId, data) => axios.post(`${API}/cycles/${cycleId}/cfd-riscuoti`, data);

// Profits (module-specific) - with pagination
export const getProfits = (module = 'futures', page = 1, limit = 50) => 
  axios.get(withPagination(withModule(`${API}/profits`, module), page, limit));
export const addProfit = (data, module = 'futures') => axios.post(withModule(`${API}/profits`, module), data);
export const updateProfit = (id, data) => axios.put(`${API}/profits/${id}`, data);
export const deleteProfit = (id) => axios.delete(`${API}/profits/${id}`);

// Prop Costs (module-specific) - with pagination
export const getPropCosts = (module = 'futures', page = 1, limit = 50) => 
  axios.get(withPagination(withModule(`${API}/prop-costs`, module), page, limit));
export const addPropCost = (data, module = 'futures') => axios.post(withModule(`${API}/prop-costs`, module), data);
export const updatePropCost = (id, data) => axios.put(`${API}/prop-costs/${id}`, data);
export const deletePropCost = (id) => axios.delete(`${API}/prop-costs/${id}`);
export const withdrawPropCosts = (amount, module = 'futures') => axios.post(withModule(`${API}/prop-costs/withdraw`, module), { amount });

// Payouts (module-specific) - with pagination
export const getPayouts = (module = 'futures', page = 1, limit = 50) => 
  axios.get(withPagination(withModule(`${API}/payouts`, module), page, limit));
export const addPayout = (data, module = 'futures') => axios.post(withModule(`${API}/payouts`, module), data);
export const updatePayout = (id, data) => axios.put(`${API}/payouts/${id}`, data);
export const deletePayout = (id) => axios.delete(`${API}/payouts/${id}`);

// User Withdrawals (module-specific) - with pagination
export const getUserWithdrawals = (module = 'futures', page = 1, limit = 50) => 
  axios.get(withPagination(withModule(`${API}/user-withdrawals`, module), page, limit));
export const addUserWithdrawal = (data, module = 'futures') => axios.post(withModule(`${API}/user-withdrawals`, module), data);
export const updateUserWithdrawal = (id, data) => axios.put(`${API}/user-withdrawals/${id}`, data);
export const deleteUserWithdrawal = (id) => axios.delete(`${API}/user-withdrawals/${id}`);

// Personal Expenses (module-specific) - with pagination
export const getPersonalExpenses = (module = 'futures', page = 1, limit = 50) => 
  axios.get(withPagination(withModule(`${API}/personal-expenses`, module), page, limit));
export const addPersonalExpense = (data, module = 'futures') => axios.post(withModule(`${API}/personal-expenses`, module), data);
export const updatePersonalExpense = (id, data) => axios.put(`${API}/personal-expenses/${id}`, data);
export const deletePersonalExpense = (id) => axios.delete(`${API}/personal-expenses/${id}`);

// Manager Payments (module-specific) - with pagination
export const getManagerPayments = (module = 'futures', page = 1, limit = 50) => 
  axios.get(withPagination(withModule(`${API}/manager-payments`, module), page, limit));
export const getManagerPaymentsTotals = (module = 'futures') => 
  axios.get(withModule(`${API}/manager-payments/totals`, module));
export const addManagerPayment = (data, module = 'futures') => axios.post(withModule(`${API}/manager-payments`, module), data);
export const updateManagerPayment = (id, data) => axios.put(`${API}/manager-payments/${id}`, data);
export const deleteManagerPayment = (id) => axios.delete(`${API}/manager-payments/${id}`);

// Saved Payment Methods - Arbitex (module-specific)
export const getSavedPaymentMethods = (module = 'futures') => axios.get(withModule(`${API}/saved-payment-methods`, module));
export const addSavedPaymentMethod = (data, module = 'futures') => axios.post(withModule(`${API}/saved-payment-methods`, module), data);
export const updateSavedPaymentMethod = (id, data) => axios.put(`${API}/saved-payment-methods/${id}`, data);
export const toggleFavoriteMethod = (id) => axios.put(`${API}/saved-payment-methods/${id}/favorite`);
export const deleteSavedPaymentMethod = (id) => axios.delete(`${API}/saved-payment-methods/${id}`);

// User Payment Methods - Pagamenti per Noi (module-specific)
export const getUserPaymentMethods = (module = 'futures') => axios.get(withModule(`${API}/user-payment-methods`, module));
export const addUserPaymentMethod = (data, module = 'futures') => axios.post(withModule(`${API}/user-payment-methods`, module), data);
export const updateUserPaymentMethod = (id, data) => axios.put(`${API}/user-payment-methods/${id}`, data);
export const toggleUserMethodFavorite = (id) => axios.put(`${API}/user-payment-methods/${id}/favorite`);
export const deleteUserPaymentMethod = (id) => axios.delete(`${API}/user-payment-methods/${id}`);

// Sandy (futures only - no module parameter needed)
export const getSandySettings = () => axios.get(`${API}/sandy/settings`);
export const updateSandyCapital = (person, capital) => axios.put(`${API}/sandy/settings/${person}?capital=${capital}`);
export const getSandyWithdrawals = () => axios.get(`${API}/sandy/withdrawals`);
export const addSandyWithdrawal = (data) => axios.post(`${API}/sandy/withdrawals`, data);
export const updateSandyWithdrawal = (id, data) => axios.put(`${API}/sandy/withdrawals/${id}`, data);
export const deleteSandyWithdrawal = (id) => axios.delete(`${API}/sandy/withdrawals/${id}`);
export const getSandyMonthlyCapitals = () => axios.get(`${API}/sandy/monthly-capitals`);
export const addSandyMonthlyCapital = (data) => axios.post(`${API}/sandy/monthly-capitals`, data);

// Sandy Capitals (for percentage calculation - futures only)
export const getSandyCapitals = () => axios.get(`${API}/settings/sandy-capitals`);
export const getCurrentSandyCapitals = () => axios.get(`${API}/settings/current-sandy-capitals`);
export const updateSandyCapitals = (data) => axios.post(`${API}/settings/sandy-capitals`, data);
export const calculateProfitSplit = (grossAmount, profitDate) => 
  axios.get(`${API}/settings/calculate-profit-split?gross_amount=${grossAmount}&profit_date=${profitDate}`);

// Dashboard (module-specific)
export const getDashboardStats = (module = 'futures') => axios.get(withModule(`${API}/dashboard/stats`, module));

// Monthly Stats (module-specific)
export const getMonthlyStats = (module = 'futures') => axios.get(withModule(`${API}/monthly-stats`, module));

// File Upload (not module-specific)
export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post(`${API}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const getFileUrl = (path, token) => {
  return `${API}/files/${path}?auth=${token}`;
};

// ============ ADMIN API ============
export const getAdminUsers = () => axios.get(`${API}/admin/users`);
export const approveUser = (userId) => axios.put(`${API}/admin/users/${userId}/approve`);
export const deleteUser = (userId) => axios.delete(`${API}/admin/users/${userId}`);
export const getUserData = (userId) => axios.get(`${API}/admin/users/${userId}/data`);
export const generateResetCode = (userId) => axios.post(`${API}/admin/users/${userId}/reset-code`);
export const uploadUserPhoto = (userId, photoFile) => {
  const formData = new FormData();
  formData.append('photo', photoFile);
  return axios.post(`${API}/admin/users/${userId}/photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const migrateSandyCandida = () => axios.post(`${API}/admin/migrate-sandy-candida`);
export const recalculateManagerAmounts = () => axios.post(`${API}/admin/recalculate-manager-amounts`);

// Password Reset (public endpoints)
export const verifyResetCode = (email, code) => axios.post(`${API}/auth/verify-reset-code`, { email, code });
export const resetPasswordWithCode = (email, code, new_password) => axios.post(`${API}/auth/reset-password`, { email, code, new_password });

// ============ ARBICSX CFD API ============

// Helper to add pagination to impersonate urls
const withImpersonatePagination = (url, impersonateUserId, page = 1, limit = 50) => {
  let result = withImpersonate(url, impersonateUserId);
  const separator = result.includes('?') ? '&' : '?';
  return `${result}${separator}page=${page}&limit=${limit}`;
};

// Settings
export const getArbicsxSettings = (impersonateUserId) => 
  axios.get(withImpersonate(`${API}/arbicsx/settings`, impersonateUserId));
export const updateArbicsxSettings = (data, impersonateUserId) => 
  axios.put(withImpersonate(`${API}/arbicsx/settings`, impersonateUserId), data);

// Cycles - with pagination
export const getArbicsxCycles = (impersonateUserId, page = 1, limit = 50) => 
  axios.get(withImpersonatePagination(`${API}/arbicsx/cycles`, impersonateUserId, page, limit));
export const createArbicsxCycle = (data, impersonateUserId) => 
  axios.post(withImpersonate(`${API}/arbicsx/cycles`, impersonateUserId), data);
export const deleteArbicsxCycle = (cycleId, impersonateUserId) => 
  axios.delete(withImpersonate(`${API}/arbicsx/cycles/${cycleId}`, impersonateUserId));
export const getArbicsxCycleDetails = (cycleId, impersonateUserId) => 
  axios.get(withImpersonate(`${API}/arbicsx/cycles/${cycleId}/details`, impersonateUserId));
// Profitto Prop - moves cycle to "In Attesa di Pagamento" with payout type
export const startAwaitingPayment = (cycleId, data, impersonateUserId) => 
  axios.post(withImpersonate(`${API}/arbicsx/cycles/${cycleId}/profit-prop`, impersonateUserId), data);
// Da Prelevare - claim payment after countdown
export const claimPayment = (cycleId, data, impersonateUserId) => 
  axios.post(withImpersonate(`${API}/arbicsx/cycles/${cycleId}/claim-payment`, impersonateUserId), data);
// Chiudi Ciclo (Bruciato) - for awaiting cycles, automatic registration
export const burnAwaitingCycle = (cycleId, data, impersonateUserId) => 
  axios.post(withImpersonate(`${API}/arbicsx/cycles/${cycleId}/burn-awaiting`, impersonateUserId), data);
export const completeArbicsxCycle = (cycleId, data, impersonateUserId) => 
  axios.post(withImpersonate(`${API}/arbicsx/cycles/${cycleId}/complete`, impersonateUserId), data);

// Credits (from payout cycles)
export const getArbicsxCredits = (impersonateUserId, page = 1, limit = 50) => 
  axios.get(withImpersonatePagination(`${API}/arbicsx/credits`, impersonateUserId, page, limit));
export const updateArbicsxCredits = (data, impersonateUserId) =>
  axios.put(withImpersonate(`${API}/arbicsx/credits`, impersonateUserId), data);

// Wallet
export const getMyWallet = (impersonateUserId) => 
  axios.get(withImpersonate(`${API}/arbicsx/my-wallet`, impersonateUserId));
export const getWalletTransactions = (userId, page = 1) => 
  axios.get(`${API}/arbicsx/users/${userId}/wallet-transactions?page=${page}`);
export const getWalletAddressBook = (userId) => 
  axios.get(`${API}/arbicsx/users/${userId}/wallet-address-book`);
export const addWalletAddress = (userId, data) => 
  axios.post(`${API}/arbicsx/users/${userId}/wallet-address-book`, data);
export const deleteWalletAddress = (userId, addressId) => 
  axios.delete(`${API}/arbicsx/users/${userId}/wallet-address-book/${addressId}`);
export const getAllWalletsSummary = () => axios.get(`${API}/arbicsx/admin/all-wallets`);

// Profile Photo
export const uploadArbicsxProfilePhoto = (photoFile, impersonateUserId) => {
  const formData = new FormData();
  formData.append('photo', photoFile);
  return axios.post(withImpersonate(`${API}/arbicsx/profile-photo`, impersonateUserId), formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteArbicsxProfilePhoto = (impersonateUserId) =>
  axios.delete(withImpersonate(`${API}/arbicsx/profile-photo`, impersonateUserId));

// Prop Incomes - with pagination
export const getArbicsxPropIncomes = (impersonateUserId, page = 1, limit = 50) => 
  axios.get(withImpersonatePagination(`${API}/arbicsx/prop-incomes`, impersonateUserId, page, limit));
export const addArbicsxPropIncome = (data, impersonateUserId) => 
  axios.post(withImpersonate(`${API}/arbicsx/prop-incomes`, impersonateUserId), data);
export const updateArbicsxPropIncome = (id, data, impersonateUserId) => 
  axios.put(withImpersonate(`${API}/arbicsx/prop-incomes/${id}`, impersonateUserId), data);
export const deleteArbicsxPropIncome = (id, impersonateUserId) => 
  axios.delete(withImpersonate(`${API}/arbicsx/prop-incomes/${id}`, impersonateUserId));

// Profits - with pagination
export const getArbicsxProfits = (impersonateUserId, page = 1, limit = 50) => 
  axios.get(withImpersonatePagination(`${API}/arbicsx/profits`, impersonateUserId, page, limit));
export const addArbicsxProfit = (data, impersonateUserId) => 
  axios.post(withImpersonate(`${API}/arbicsx/profits`, impersonateUserId), data);
export const updateArbicsxProfit = (id, data, impersonateUserId) => 
  axios.put(withImpersonate(`${API}/arbicsx/profits/${id}`, impersonateUserId), data);
export const deleteArbicsxProfit = (id, impersonateUserId) => 
  axios.delete(withImpersonate(`${API}/arbicsx/profits/${id}`, impersonateUserId));

// Prop Costs - with pagination
export const getArbicsxPropCosts = (impersonateUserId, page = 1, limit = 50) => 
  axios.get(withImpersonatePagination(`${API}/arbicsx/prop-costs`, impersonateUserId, page, limit));
export const addArbicsxPropCost = (data, impersonateUserId) => 
  axios.post(withImpersonate(`${API}/arbicsx/prop-costs`, impersonateUserId), data);
export const updateArbicsxPropCost = (id, data, impersonateUserId) => 
  axios.put(withImpersonate(`${API}/arbicsx/prop-costs/${id}`, impersonateUserId), data);
export const deleteArbicsxPropCost = (id, impersonateUserId) => 
  axios.delete(withImpersonate(`${API}/arbicsx/prop-costs/${id}`, impersonateUserId));
export const withdrawArbicsxPropCosts = (amount, impersonateUserId) => 
  axios.post(withImpersonate(`${API}/arbicsx/prop-costs/withdraw`, impersonateUserId), { amount });

// User Withdrawals (Pagamenti per Noi) - with pagination
export const getArbicsxUserWithdrawals = (impersonateUserId, page = 1, limit = 50) => 
  axios.get(withImpersonatePagination(`${API}/arbicsx/user-withdrawals`, impersonateUserId, page, limit));
export const addArbicsxUserWithdrawal = (data, impersonateUserId) => 
  axios.post(withImpersonate(`${API}/arbicsx/user-withdrawals`, impersonateUserId), data);
export const updateArbicsxUserWithdrawal = (id, data, impersonateUserId) => 
  axios.put(withImpersonate(`${API}/arbicsx/user-withdrawals/${id}`, impersonateUserId), data);
export const deleteArbicsxUserWithdrawal = (id, impersonateUserId) => 
  axios.delete(withImpersonate(`${API}/arbicsx/user-withdrawals/${id}`, impersonateUserId));

// Personal Expenses (Spese) - with pagination
export const getArbicsxPersonalExpenses = (impersonateUserId, page = 1, limit = 50) => 
  axios.get(withImpersonatePagination(`${API}/arbicsx/personal-expenses`, impersonateUserId, page, limit));
export const addArbicsxPersonalExpense = (data, impersonateUserId) => 
  axios.post(withImpersonate(`${API}/arbicsx/personal-expenses`, impersonateUserId), data);
export const updateArbicsxPersonalExpense = (id, data, impersonateUserId) => 
  axios.put(withImpersonate(`${API}/arbicsx/personal-expenses/${id}`, impersonateUserId), data);
export const deleteArbicsxPersonalExpense = (id, impersonateUserId) => 
  axios.delete(withImpersonate(`${API}/arbicsx/personal-expenses/${id}`, impersonateUserId));

// Payment Methods
export const getArbicsxPaymentMethods = (impersonateUserId) => 
  axios.get(withImpersonate(`${API}/arbicsx/payment-methods`, impersonateUserId));
export const addArbicsxPaymentMethod = (data, impersonateUserId) => 
  axios.post(withImpersonate(`${API}/arbicsx/payment-methods`, impersonateUserId), data);
export const updateArbicsxPaymentMethod = (id, data, impersonateUserId) => 
  axios.put(withImpersonate(`${API}/arbicsx/payment-methods/${id}`, impersonateUserId), data);
export const toggleArbicsxPaymentMethodFavorite = (id, impersonateUserId) => 
  axios.put(withImpersonate(`${API}/arbicsx/payment-methods/${id}/favorite`, impersonateUserId));
export const deleteArbicsxPaymentMethod = (id, impersonateUserId) => 
  axios.delete(withImpersonate(`${API}/arbicsx/payment-methods/${id}`, impersonateUserId));

// Dashboard Stats
export const getArbicsxDashboardStats = (impersonateUserId) => 
  axios.get(withImpersonate(`${API}/arbicsx/dashboard/stats`, impersonateUserId));

// Monthly Stats
export const getArbicsxMonthlyStats = (impersonateUserId) => 
  axios.get(withImpersonate(`${API}/arbicsx/monthly-stats`, impersonateUserId));

// Monthly Capitals
export const addArbicsxMonthlyCapital = (data, impersonateUserId) => 
  axios.post(withImpersonate(`${API}/arbicsx/monthly-capitals`, impersonateUserId), data);

// ============ PROFILE & ACCOUNT MANAGEMENT ============
export const changePassword = (data) => axios.put(`${API}/auth/change-password`, data);
export const changeEmail = (data) => axios.put(`${API}/auth/change-email`, data);
export const uploadProfilePhoto = (photoFile) => {
  const formData = new FormData();
  formData.append('photo', photoFile);
  return axios.post(`${API}/auth/profile-photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteProfilePhoto = () => axios.delete(`${API}/auth/profile-photo`);

// ============ ADMIN USER MANAGEMENT ============
export const getAllUsers = () => axios.get(`${API}/admin/users`);
export const toggleUserStatus = (userId, isApproved) => 
  axios.put(`${API}/admin/users/toggle`, { user_id: userId, is_approved: isApproved });
export const generateRecoveryCode = () => axios.post(`${API}/admin/generate-recovery-code`);

// ============ ADMIN SECRET PANEL ============
export const getMasterDashboard = () => axios.get(`${API}/admin/master-dashboard`);
export const getAdminNotes = () => axios.get(`${API}/admin/notes`);
export const updateAdminNotes = (content) => axios.put(`${API}/admin/notes`, { content });
export const getAppStatus = () => axios.get(`${API}/admin/app-status`);
export const updateAppStatus = (data) => axios.put(`${API}/admin/app-status`, data);
export const getActivityLog = (limit = 100) => axios.get(`${API}/admin/activity-log?limit=${limit}`);
export const broadcastMessage = (message, target) => 
  axios.post(`${API}/admin/broadcast-message`, { message, target });

// ============ USER MESSAGES ============
export const getUserMessages = () => axios.get(`${API}/user/messages`);
export const dismissMessage = (messageId) => axios.post(`${API}/user/messages/${messageId}/dismiss`);
export const getPublicAppStatus = () => axios.get(`${API}/app/status`);

// ============ ARBICSX SUBSCRIPTION MANAGEMENT ============
export const getUsersWithSubscriptions = () => axios.get(`${API}/arbicsx/users/subscriptions`);
export const getUserPaymentHistory = (userId) => axios.get(`${API}/arbicsx/users/${userId}/payments`);
export const renewSubscription = (userId, months, amount = 0) => 
  axios.post(`${API}/arbicsx/users/renew`, { user_id: userId, months, amount });
export const setUserFree = (userId, isFree) => 
  axios.post(`${API}/arbicsx/users/set-free`, { user_id: userId, is_free: isFree });
export const setUserExpiry = (userId, expiryDate) => 
  axios.post(`${API}/arbicsx/users/set-expiry`, { user_id: userId, expiry_date: expiryDate });
export const blockUser = (userId, isBlocked) => 
  axios.post(`${API}/arbicsx/users/block`, { user_id: userId, is_blocked: isBlocked });
export const suspendUser = (userId, isSuspended) => 
  axios.post(`${API}/arbicsx/users/suspend`, { user_id: userId, is_suspended: isSuspended });
export const setUserManagementType = (userId, managementType) => 
  axios.post(`${API}/arbicsx/users/set-management-type`, { user_id: userId, management_type: managementType });
export const setUserDelegationType = (userId, delegationType) => 
  axios.post(`${API}/arbicsx/users/set-delegation-type`, { user_id: userId, delegation_type: delegationType });
export const sendQuickActionMessage = (userId, actionType, amount, numAccounts, method, whoDoesIt) => 
  axios.post(`${API}/arbicsx/users/quick-action`, { 
    user_id: userId, 
    action_type: actionType, 
    amount, 
    num_accounts: numAccounts, 
    method, 
    who_does_it: whoDoesIt 
  });
export const sendBroadcastMessage = (target, message) => 
  axios.post(`${API}/arbicsx/users/broadcast-message`, { target, message });
export const setUserTrial = (userId, trialDays) => 
  axios.post(`${API}/arbicsx/users/set-trial`, { user_id: userId, trial_days: trialDays });
export const resetUserTelegram = (userId) => 
  axios.post(`${API}/arbicsx/users/reset-telegram`, { user_id: userId });
export const endUserTrial = (userId) => 
  axios.post(`${API}/arbicsx/users/end-trial`, { user_id: userId });

// Dashboard Credentials (Admin only)
export const getDashboardCredentials = (userId) => 
  axios.get(`${API}/arbicsx/users/${userId}/dashboard-credentials`);
export const updateDashboardCredentials = (userId, email, password) => 
  axios.put(`${API}/arbicsx/users/${userId}/dashboard-credentials`, { arbicsx_email: email, arbicsx_password: password });

export const createGiftCode = (months, expiresAt = null) => 
  axios.post(`${API}/arbicsx/gift-codes`, { months, expires_at: expiresAt });
export const getGiftCodes = () => axios.get(`${API}/arbicsx/gift-codes`);
export const getGiftCodesDebug = () => axios.get(`${API}/arbicsx/gift-codes/debug`);
export const redeemGiftCode = (code) => axios.post(`${API}/arbicsx/redeem-gift-code`, { code });
export const redeemGiftCodeDebug = (code) => axios.post(`${API}/arbicsx/redeem-gift-code/debug`, { code });
export const getMySubscription = () => axios.get(`${API}/arbicsx/my-subscription`);
// User requests
export const createUserRequest = (requestType, message = '') => 
  axios.post(`${API}/arbicsx/user-requests`, { request_type: requestType, message });
export const getUserRequests = () => axios.get(`${API}/arbicsx/user-requests`);
export const handleUserRequest = (userId, requestId, action) => 
  axios.post(`${API}/arbicsx/user-requests/action`, { user_id: userId, request_id: requestId, action });
// Notifications
export const getNotifications = () => axios.get(`${API}/arbicsx/notifications`);
export const getUnreadNotifications = () => axios.get(`${API}/arbicsx/notifications/unread`);
export const markNotificationRead = (notificationId) => 
  axios.post(`${API}/arbicsx/notifications/${notificationId}/read`);
export const markAllNotificationsRead = () => axios.post(`${API}/arbicsx/notifications/read-all`);

// Telegram Test Notifications (admin only)
export const testTelegramNotifications = () => axios.post(`${API}/telegram/test-all-notifications`);
export const debugUserTelegram = (userId) => axios.get(`${API}/telegram/debug-user/${userId}`);

// Admin - Get user completed cycles
export const getUserCompletedCycles = (userId) => axios.get(`${API}/admin/arbicsx/users/${userId}/completed-cycles`);

// ============ LINKED PROFILES (cambio profilo) ============
export const getLinkedProfiles = () => axios.get(`${API}/auth/linked-profiles`);
export const switchProfileApi = (targetUserId) => axios.post(`${API}/auth/switch-profile`, { target_user_id: targetUserId });
export const getProfileGroups = () => axios.get(`${API}/admin/profile-groups`);
export const getProfileGroupSuggestions = (userId) => axios.get(`${API}/admin/profile-groups/suggestions/${userId}`);
export const upsertProfileGroup = (userIds, primaryUserId, groupId = null) =>
  axios.post(`${API}/admin/profile-groups`, { user_ids: userIds, primary_user_id: primaryUserId, group_id: groupId });
export const dissolveProfileGroup = (groupId) => axios.delete(`${API}/admin/profile-groups/${groupId}`);
export const unlinkProfileFromGroup = (groupId, userId, newPrimaryUserId = null) =>
  axios.delete(`${API}/admin/profile-groups/${groupId}/users/${userId}${newPrimaryUserId ? `?new_primary_user_id=${newPrimaryUserId}` : ''}`);

// ============ FAMILY GROUPS (riepilogo famiglia) ============
export const getFamilySummary = (impersonateUserId) =>
  axios.get(withImpersonate(`${API}/auth/family-summary`, impersonateUserId));
export const getFamilyGroups = () => axios.get(`${API}/admin/family-groups`);
export const upsertFamilyGroup = (userIds, groupId = null) =>
  axios.post(`${API}/admin/family-groups`, { user_ids: userIds, group_id: groupId });
export const dissolveFamilyGroup = (groupId) => axios.delete(`${API}/admin/family-groups/${groupId}`);
export const removeFamilyMember = (groupId, userId) => axios.delete(`${API}/admin/family-groups/${groupId}/users/${userId}`);
export const setFamilyView = (groupId, userId, enabled) =>
  axios.put(`${API}/admin/family-groups/${groupId}/users/${userId}/family-view`, { enabled });

// Admin - Aggregate Summary
export const getAdminAggregateSummary = () => axios.get(`${API}/arbicsx/admin/aggregate-summary`);
export const getAdminAggregatePreferences = () => axios.get(`${API}/arbicsx/admin/aggregate-preferences`);
export const updateAdminAggregatePreferences = (data) => axios.put(`${API}/arbicsx/admin/aggregate-preferences`, data);
